import { useState, useEffect } from 'react';
import { Box, Container, Group, Button, Burger, Drawer, Stack, Badge } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconArrowRight } from '@tabler/icons-react';
import { ManifestusLogo } from './ManifestusLogo';
import { useAuthStore } from '../../store/auth';
import { trackNavClick, trackCTAClick } from '../../analytics';

const LANDING_SECTIONS = ['how-it-works', 'capabilities'] as const;
type LandingSection = (typeof LANDING_SECTIONS)[number];

function useScrollSpy(enabled: boolean): LandingSection | null {
  const [active, setActive] = useState<LandingSection | null>(null);

  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return;
    }

    const observers: IntersectionObserver[] = [];
    const visible = new Map<string, boolean>();

    function setup() {
      for (const id of LANDING_SECTIONS) {
        const el = document.getElementById(id);
        if (!el) return false;

        const observer = new IntersectionObserver(
          ([entry]) => {
            visible.set(id, entry.isIntersecting);
            const found = LANDING_SECTIONS.find((s) => visible.get(s));
            setActive(found ?? null);
          },
          { threshold: 0.15 },
        );
        observer.observe(el);
        observers.push(observer);
      }
      return true;
    }

    if (!setup()) {
      const timer = setTimeout(setup, 300);
      return () => {
        clearTimeout(timer);
        observers.forEach((o) => o.disconnect());
      };
    }

    return () => observers.forEach((o) => o.disconnect());
  }, [enabled]);

  return active;
}

const NAV_ITEMS = [
  { label: 'How It Works', hash: 'how-it-works' },
  { label: 'Features', hash: 'capabilities' },
  { label: 'Pricing', path: '/pricing' },
] as const;

export function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [scrolled, setScrolled] = useState(false);
  const [opened, { toggle, close }] = useDisclosure(false);

  const isLanding = pathname === '/';

  const activeSection = useScrollSpy(isLanding);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (item: (typeof NAV_ITEMS)[number]) => {
    close();
    if ('path' in item && item.path) {
      trackNavClick(item.label);
      navigate(item.path);
    } else if ('hash' in item && item.hash) {
      trackNavClick(item.label);
      if (isLanding) {
        const el = document.getElementById(item.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/#' + item.hash);
      }
    }
  };

  const isActive = (item: (typeof NAV_ITEMS)[number]) => {
    if ('path' in item && item.path) return pathname === item.path;
    if ('hash' in item && item.hash) return activeSection === item.hash;
    return false;
  };

  const handleCTA = () => {
    close();
    if (isAuthenticated) {
      trackCTAClick('Dashboard', 'navbar', '/dashboard');
      navigate('/dashboard');
    } else {
      trackCTAClick('Get Started', 'navbar', '/login');
      navigate('/login');
    }
  };

  const ctaLabel = isAuthenticated ? 'Dashboard' : 'Get Started';

  return (
    <Box
      component="nav"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,.85)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,.06)' : '1px solid transparent',
        transition: 'all .3s cubic-bezier(.4,0,.2,1)',
      }}
    >
      <Container size="xl">
        <Group justify="space-between" h={60} align="center">
          {/* Left: logo + nav links */}
          <Group gap={24} align="center">
            <Group gap={10} align="center" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
              <ManifestusLogo h={20} />
              <Badge variant="light" color="violet" size="xs" radius="xl" visibleFrom="sm" style={{ textTransform: 'none', fontWeight: 500, fontSize: 10, paddingInline: 8 }}>
                Alpha
              </Badge>
            </Group>

            <Group gap={2} visibleFrom="sm" style={{ marginTop: 2 }}>
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.label}
                  variant="subtle"
                  color="gray"
                  size="compact-sm"
                  onClick={() => handleNav(item)}
                  style={{
                    fontWeight: 500,
                    fontSize: 13.5,
                    color: isActive(item) ? '#0a0a0a' : '#64748b',
                    letterSpacing: '-.01em',
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Group>
          </Group>

          {/* Right: CTA + burger */}
          <Group gap={8}>
            <Button
              size="compact-md"
              color="dark"
              variant="filled"
              radius="xl"
              onClick={handleCTA}
              rightSection={<IconArrowRight size={14} />}
              visibleFrom="sm"
              style={{ fontWeight: 600, paddingInline: 18, fontSize: 13, height: 36 }}
            >
              {ctaLabel}
            </Button>

            <Burger
              opened={opened}
              onClick={toggle}
              size="sm"
              hiddenFrom="sm"
              color="#0a0a0a"
            />
          </Group>
        </Group>
      </Container>

      {/* Mobile drawer */}
      <Drawer
        opened={opened}
        onClose={close}
        position="top"
        size="auto"
        withCloseButton={false}
        styles={{
          body: { padding: '16px 24px 24px' },
          content: { background: 'rgba(255,255,255,.95)', backdropFilter: 'blur(16px)' },
          overlay: { background: 'rgba(0,0,0,.2)' },
        }}
      >
        <Stack gap={4}>
          {NAV_ITEMS.map((item) => (
            <Button
              key={item.label}
              variant="subtle"
              color="gray"
              size="md"
              justify="flex-start"
              fullWidth
              onClick={() => handleNav(item)}
              style={{
                fontWeight: isActive(item) ? 600 : 500,
                fontSize: 15,
                color: isActive(item) ? '#0a0a0a' : '#64748b',
              }}
            >
              {item.label}
            </Button>
          ))}
          <Button
            size="md"
            color="dark"
            variant="filled"
            radius="xl"
            fullWidth
            mt={8}
            onClick={handleCTA}
            rightSection={<IconArrowRight size={16} />}
            style={{ fontWeight: 600, fontSize: 14, height: 44 }}
          >
            {ctaLabel}
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}
